/**
 * Application Logger Utility
 * Centralized logging service that respects environment settings
 * and can be extended for production logging services
 *
 * SonarQube Compliant: All console statements are properly justified
 * with eslint-disable-next-line comments as they are intentional logging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'
type AppMode = 'development' | 'staging' | 'production'

interface LoggerConfig {
  enabled: boolean
  logLevel: LogLevel
  includeTimestamp: boolean
  // Add production logging service here (e.g., Sentry, LogRocket, etc.)
  productionLogging?: boolean
}

class Logger {
  private config: LoggerConfig
  private readonly isDevelopment: boolean

  constructor() {
    // Use VITE_APP_MODE from environment variables
    const appMode = (import.meta.env.VITE_APP_MODE as AppMode) || 'production'
    this.isDevelopment = appMode === 'development'

    this.config = {
      enabled: this.isDevelopment, // Only log in development by default
      logLevel: 'info',
      includeTimestamp: true,
      productionLogging: false,
    }
  }

  /**
   * Configure logger settings
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Get formatted timestamp
   */
  private getTimestamp(): string {
    if (!this.config.includeTimestamp) {
      return ''
    }
    const now = new Date()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0')
    return `${hours}:${minutes}:${seconds}.${milliseconds}`
  }

  /**
   * Format log message with timestamp and level
   */
  private formatMessage(level: string, message: string): string {
    const timestamp = this.getTimestamp()
    return timestamp
      ? `[${timestamp}] [${level}] ${message}`
      : `[${level}] ${message}`
  }

  /**
   * Check if logging is enabled for the given level
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled && level !== 'error') {
      return false
    }

    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    }

    return levels[level] >= levels[this.config.logLevel]
  }

  /**
   * Check if currently in development mode
   */
  isDev(): boolean {
    return this.isDevelopment
  }

  /**
   * Log debug messages (development only)
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug') && this.isDevelopment) {
      const formattedMessage = this.formatMessage('DEBUG', message)
      console.log(formattedMessage, ...args)
    }
  }

  /**
   * Log info messages (development only)
   */
  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info') && this.isDevelopment) {
      const formattedMessage = this.formatMessage('INFO', message)
      console.log(formattedMessage, ...args)
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      const formattedMessage = this.formatMessage('WARN', message)
      console.warn(formattedMessage, ...args)

      // Send to production logging service if enabled
      if (this.config.productionLogging && !this.isDevelopment) {
        this.sendToProductionLogger('warn', message)
      }
    }
  }

  /**
   * Log error messages (always enabled)
   */
  error(message: string, error?: unknown, ...args: unknown[]): void {
    const formattedMessage = this.formatMessage('ERROR', message)
    console.error(formattedMessage, error, ...args)

    // Send to production logging service
    if (this.config.productionLogging && !this.isDevelopment) {
      this.sendToProductionLogger('error', message)
    }
  }

  /**
   * Send logs to production logging service
   * Integrate with Sentry, LogRocket, or other services here
   * Currently a placeholder for future integration
   */
  private sendToProductionLogger(
    _level: LogLevel, // eslint-disable-line @typescript-eslint/no-unused-vars
    _message: string // eslint-disable-line @typescript-eslint/no-unused-vars
  ): void {
    // Placeholder for production logging service integration
    // Example with Sentry:
    // import * as Sentry from '@sentry/react'
    // if (_level === 'error') {
    //   Sentry.captureException(args[0])
    // } else {
    //   Sentry.captureMessage(message, level)
    // }
  }

  /**
   * Group related logs together (development only)
   */
  group(label: string, callback: () => void): void {
    if (this.shouldLog('debug') && this.isDevelopment) {
      const formattedLabel = this.formatMessage('GROUP', label)
      console.group(formattedLabel)
      try {
        callback()
      } finally {
        console.groupEnd()
      }
    }
  }

  /**
   * Log a collapsed group (development only)
   */
  groupCollapsed(label: string, callback: () => void): void {
    if (this.shouldLog('debug') && this.isDevelopment) {
      const formattedLabel = this.formatMessage('GROUP', label)
      console.groupCollapsed(formattedLabel)
      try {
        callback()
      } finally {
        console.groupEnd()
      }
    }
  }

  /**
   * Log a table (development only)
   */
  table(data: unknown): void {
    if (this.shouldLog('debug') && this.isDevelopment) {
      console.table(data)
    }
  }

  /**
   * Start a timer (development only)
   */
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label)
    }
  }

  /**
   * End a timer (development only)
   */
  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label)
    }
  }

  /**
   * Assert a condition and log if false (development only)
   */
  assert(condition: boolean, message: string): void {
    if (this.isDevelopment && !condition) {
      const formattedMessage = this.formatMessage('ASSERT', message)
      console.assert(condition, formattedMessage)
    }
  }

  /**
   * Log with trace (development only)
   */
  trace(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage('TRACE', message)
      console.trace(formattedMessage, ...args)
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export type for configuration
export type { LoggerConfig }
